import { WerknlLayout } from 'clients/UserClient/types';
import { logger } from 'config/logger';
import { useGTM } from 'hooks/gtm/useGTM';
import { useLocalStorage } from 'hooks/shared/useLocalStorage';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { useCallback, useContext } from 'react';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { CampaignJobboard } from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.types';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { WERKNL_ACTIONS, WERKNL_LAYOUT_CHOSEN } from 'shared/legacy/contexts/UserContext/types';
import { UserContext } from 'shared/legacy/contexts/UserContext/UserContext';
import { NoCreditsAvailableForMonsterboard, StopExecution } from 'shared/legacy/exceptions';
import { BadRequestException } from 'shared/legacy/exceptions/BadRequestException';
import { IndeedCandidateAlreadyContactedException } from 'shared/legacy/exceptions/IndeedCandidateAlreadyContactedException';
import { IndeedCandidateEngagementException } from 'shared/legacy/exceptions/IndeedCandidateEngagementException';
import { IndeedCandidateRecentlyDeclinedException } from 'shared/legacy/exceptions/IndeedCandidateRecentlyDeclinedException';
import { IndeedCandidateSubscriptionMismatchException } from 'shared/legacy/exceptions/IndeedCandidateSubscriptionMismatchException';
import { IndeedBadUserInputException } from 'shared/legacy/exceptions/IndeedException';
import { LinkedinFreeInviteLimitReachedException } from 'shared/legacy/exceptions/LinkedinException';
import { useCandidateOrchestrator } from '../useCandidateOrchestrator';
import { UseCandidateAction } from './types';

const allowedErrorsToThrow = [
  NoCreditsAvailableForMonsterboard,
  LinkedinFreeInviteLimitReachedException,
  IndeedBadUserInputException,
  IndeedCandidateAlreadyContactedException,
  IndeedCandidateEngagementException,
  IndeedCandidateRecentlyDeclinedException,
  IndeedCandidateSubscriptionMismatchException,
  BadRequestException,
];

export const useCandidateAction: UseCandidateAction = (fc, extras) => {
  const gtm = useGTM();
  const { currentUser } = useContext(UserContext);
  const { createSnackbar } = useContext(SnackbarContext);
  const { dictionary } = useContext(LocalizationContext);
  const pageContext = usePageContext();
  const candidateOrchestrator = useCandidateOrchestrator();
  const { setValue } = useLocalStorage<number>(`${WERKNL_ACTIONS}-${currentUser?.id}`, 0);
  const { value: werknlLayoutChosen } = useLocalStorage<boolean>(`${WERKNL_LAYOUT_CHOSEN}-${currentUser?.id}`);

  return ({ candidate, setIsLoading, registerWerknlActivity }) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useCallback(
      async (params) => {
        setIsLoading(true);

        try {
          if (!candidate) {
            throw new TypeError('Something went wrong while creating actions for this candidate');
          }
          const fcParams = { candidate, ...params };

          const fcResponse = await fc(fcParams);

          const co = candidateOrchestrator(candidate);

          await extras?.onSuccess?.({ fcParams: { candidate, ...params }, gtm })?.({
            data: fcResponse,
            pageContext,
            candidateOrchestrator: co,
          });

          if (co.campaign.get.listCount() === 0 && pageContext === PageContext.Campaign) {
            co.campaign.resetCurrentQuery();
          }
          if ((pageContext === PageContext.RatedCandidates && co.ratedCandidates.get.listCount()) === 0) {
            co.ratedCandidates.resetCurrentQuery();
          }

          return fcResponse;
        } catch (e) {
          try {
            if (!candidate) {
              throw new TypeError('Something went wrong while creating actions for this candidate');
            }

            if (extras?.onError) {
              const co = candidateOrchestrator(candidate);

              await extras.onError({ fcParams: { candidate, ...params }, gtm })?.({
                data: e,
                pageContext,
                candidateOrchestrator: co,
              });

              if (co.campaign.get.listCount() === 0 && pageContext === PageContext.Campaign) {
                co.campaign.resetCurrentQuery();
              }
              if ((pageContext === PageContext.RatedCandidates && co.ratedCandidates.get.listCount()) === 0) {
                co.ratedCandidates.resetCurrentQuery();
              }
            } else {
              throw e;
            }
          } catch (e) {
            if (allowedErrorsToThrow.some((error) => e instanceof error)) {
              throw e;
            }

            if (!(e instanceof StopExecution)) {
              logger.error(e);

              throw new StopExecution(e instanceof Error ? e.message : undefined);
            }
          }
        } finally {
          if (
            registerWerknlActivity &&
            !werknlLayoutChosen &&
            currentUser?.werknl_layout === WerknlLayout.New &&
            candidate?.activeJobBoard === CampaignJobboard.Werknl
          ) {
            setValue((prevState) => (prevState ?? 0) + 1);
          }

          setIsLoading(false);
        }
      },
      [gtm, createSnackbar, dictionary, candidate, werknlLayoutChosen, currentUser?.werknl_layout],
    );
  };
};
